import { CalendarTenantScopeService } from '@modules/calendar-app/services/calendar-tenant-scope.service';
import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { type EventsFilters } from '@modules/calendar-app/types/events-filters';
import { signalCarriesNoChanges } from '@modules/calendar-app/utils/event-rsvp.util';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { StitchEventsService } from '@modules/common/services/stitch-events-service';
import { getErrorMessage } from '@frontendlib/common/utils/ErrorMessage';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { SignalStitchEventType } from '@sharedlib/common/types/signal-type';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { StitchSignalEventPayload } from '@sharedlib/dtos/dto/events/StitchSignalEventPayload';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { pipe, switchMap, tap } from 'rxjs';

type StickyCalendarEventsState = {
  events: CalendarEventSummaryDto[];
  isLoading: boolean;
  error: string | null;
  lastFilters: EventsFilters | null;
  lastOptions: {
    withTags: boolean;
    withKnots: boolean;
    withConnections: boolean;
    knotsSource?: KnotSource;
  } | null;
};

const initialState: StickyCalendarEventsState = {
  events: [],
  isLoading: true,
  error: null,
  lastFilters: null,
  lastOptions: null,
};

@Injectable()
export class StickyCalendarEventsStore extends signalStore(
  withState(initialState),
  withComputed((store) => ({
    anyStickyFilterEnabled: computed((): boolean => {
      const filters = store.lastFilters();
      return !!(
        filters &&
        (filters.pinnedOnTop || filters.flaggedOnTop || filters.followedOnTop || filters.snoozedOnTop)
      );
    }),

    _stickyAddEventTypes: computed(() => {
      const filters = store.lastFilters();
      if (!filters) {
        return [];
      }
      const types: SignalStitchEventType[] = [];
      if (filters.pinnedOnTop) {
        types.push(SignalStitchEventType.PINNED);
      }
      if (filters.flaggedOnTop) {
        types.push(SignalStitchEventType.FLAGGED);
      }
      if (filters.followedOnTop) {
        types.push(SignalStitchEventType.FOLLOW_TIMEOUT);
      }
      if (filters.snoozedOnTop) {
        types.push(SignalStitchEventType.SNOOZE_TIMEOUT);
      }
      return types;
    }),

    _stickyRemoveEventTypes: computed(() => {
      const filters = store.lastFilters();
      if (!filters) {
        return [];
      }
      const types: SignalStitchEventType[] = [
        SignalStitchEventType.MOVED_TO_TRASH,
        SignalStitchEventType.ARCHIVED,
        SignalStitchEventType.SNOOZED,
        SignalStitchEventType.DELETED,
      ];
      if (filters.pinnedOnTop) {
        types.push(SignalStitchEventType.UNPINNED);
      }
      if (filters.flaggedOnTop) {
        types.push(SignalStitchEventType.UNFLAGGED);
      }
      if (filters.followedOnTop) {
        types.push(SignalStitchEventType.UNFOLLOWED);
      }
      if (filters.snoozedOnTop) {
        types.push(SignalStitchEventType.UNSNOOZED);
      }
      return types;
    }),
  })),
  withMethods((store) => {
    const eventsService = inject(EventsService);

    const loadEvents = rxMethod<{
      filters: EventsFilters;
      withTags?: boolean;
      withKnots?: boolean;
      withConnections?: boolean;
      knotsSource?: KnotSource;
    }>(
      pipe(
        tap(({ filters, withTags = false, withKnots = false, withConnections = false, knotsSource }) => {
          patchState(store, {
            isLoading: true,
            error: null,
            lastFilters: filters,
            lastOptions: { withTags, withKnots, withConnections, knotsSource },
          });
        }),
        switchMap(({ filters, withTags = false, withKnots = false, withConnections = false, knotsSource }) =>
          eventsService.getStickyEvents(filters, {
            withTags,
            withKnots,
            withConnections,
            knotsSource,
          }),
        ),
        tap({
          next: (events: CalendarEventSummaryDto[]) => patchState(store, { events, isLoading: false }),
          error: (error: Error) => patchState(store, { error: getErrorMessage(error), isLoading: false }),
        }),
      ),
    );

    const refresh = (): void => {
      const filters = store.lastFilters();
      const options = store.lastOptions();

      if (filters && options) {
        loadEvents({
          filters: filters,
          withTags: options.withTags,
          withKnots: options.withKnots,
          withConnections: options.withConnections,
          knotsSource: options.knotsSource,
        });
      }
    };

    const isStickyItem = (item: Partial<StitchItemSummaryDto>): boolean => {
      const filters = store.lastFilters();
      if (!filters) {
        return false;
      }

      return (
        (filters.pinnedOnTop && item.pinned) ||
        (filters.flaggedOnTop && item.flagged) ||
        (filters.followedOnTop && item.followed && item.followed.getTime() <= Date.now()) ||
        (filters.snoozedOnTop && item.snoozed && item.snoozed.getTime() <= Date.now())
      );
    };

    return {
      loadEvents: loadEvents,

      refresh: refresh,

      reset: (): void => {
        patchState(store, initialState);
      },

      _handleEventAdded: (signal: StitchSignalEventPayload<StitchType.EVENT>): void => {
        if (store._stickyAddEventTypes().includes(signal.eventType)) {
          refresh();
          return;
        }

        if (signal.eventType === SignalStitchEventType.CREATED && signal.items.some((item) => isStickyItem(item))) {
          refresh();
          return;
        }

        if (store.anyStickyFilterEnabled()) {
          // this may be RESTORED_FROM_TRASH or UNARCHIVED
          // so we don't know if related item matches "sticky" criteria (no items in event payload)
          // while filters for sticky are seleted - so just reoad
          refresh();
          return;
        }
      },

      _handleEventRemoved: (signal: StitchSignalEventPayload<StitchType.EVENT>): void => {
        if (!store._stickyRemoveEventTypes().includes(signal.eventType)) {
          return;
        }

        const currentEvents = store.events();
        const idsToRemove = signal.ids.filter((id) => currentEvents.some((event) => event.id === id));
        if (idsToRemove.length > 0) {
          const newEvents = currentEvents.filter((event) => !idsToRemove.includes(event.id));
          patchState(store, { events: newEvents });
        }
      },

      _handleEventUpdated: (
        signal: StitchSignalEventPayload<StitchType.EVENT, SignalStitchEventType.UPDATED>,
      ): void => {
        if (signalCarriesNoChanges(signal)) {
          // With no sticky filter on, the backend returns [] by construction — the `return` still has
          // to be unconditional, or a bare `{ itemType }` change falls through to the patch below.
          if (store.anyStickyFilterEnabled()) {
            refresh();
          }
          return;
        }

        if (isStickyItem(signal.changes) && !store.events().some((event) => signal.ids.includes(event.id))) {
          // sticky change detected in item outside of loaded list - reload
          refresh();
          return;
        }

        let hasChanges = false;
        const newEvents = store.events().map((event) => {
          if (signal.ids.includes(event.id)) {
            event = { ...event, ...signal.changes };
            hasChanges = true;
          }
          return event;
        });
        if (hasChanges) {
          patchState(store, { events: newEvents });
        }
      },
    };
  }),
  withHooks({
    onInit(store): void {
      const stitchEventsService = inject(StitchEventsService);
      const spaceContextStore = inject(SpaceContextStore);
      const calendarTenantScope = inject(CalendarTenantScopeService, { optional: true });
      const signalSpaceIds = () =>
        [spaceContextStore.contextId(), calendarTenantScope?.effectiveTenantId()].filter((id): id is string => !!id);
      const destroyRef = inject(DestroyRef);

      stitchEventsService
        .getEvents(signalSpaceIds, StitchType.EVENT, [
          SignalStitchEventType.PINNED,
          SignalStitchEventType.FLAGGED,
          SignalStitchEventType.SNOOZE_TIMEOUT,
          SignalStitchEventType.FOLLOW_TIMEOUT,

          SignalStitchEventType.CREATED,
          SignalStitchEventType.RESTORED_FROM_TRASH,
          SignalStitchEventType.UNARCHIVED,
        ])
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((signal) => {
          store._handleEventAdded(signal);
        });

      stitchEventsService
        .getEvents(signalSpaceIds, StitchType.EVENT, [SignalStitchEventType.UPDATED])
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((signal) => {
          store._handleEventUpdated(signal);
        });

      stitchEventsService
        .getEvents(signalSpaceIds, StitchType.EVENT, [
          SignalStitchEventType.UNPINNED,
          SignalStitchEventType.MOVED_TO_TRASH,
          SignalStitchEventType.ARCHIVED,
          SignalStitchEventType.SNOOZED,
          SignalStitchEventType.DELETED,
          SignalStitchEventType.UNFLAGGED,
          SignalStitchEventType.UNSNOOZED,
          SignalStitchEventType.UNFOLLOWED,
        ])
        .pipe(takeUntilDestroyed(destroyRef))
        .subscribe((signal) => {
          store._handleEventRemoved(signal);
        });
    },
  }),
) {}
